import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["wrapper", "input", "panel", "list", "spinner"]
  static values = { url: String }

  connect() {
    this.timeout = null
    this.hideTimeout = null
  }

  onInput() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchResults(this.inputTarget.value), 280)
  }

  onFocus() {
    this.activateWrapper()
    if (this.inputTarget.value.trim().length >= 2) {
      this.fetchResults(this.inputTarget.value)
    }
  }

  onBlur() {
    this.hideTimeout = setTimeout(() => this.hidePanel(), 200)
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.hidePanel()
    }
  }

  async fetchResults(q) {
    const query = (q || "").trim()
    if (query.length < 2) {
      this.hidePanel()
      return
    }

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query)

    this.setSearching(true)
    try {
      const res = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return
      const data = await res.json()
      this.renderResults(data.results || [])
    } catch (_) {
      this.hidePanel()
    } finally {
      this.setSearching(false)
    }
  }

  renderResults(results) {
    clearTimeout(this.hideTimeout)
    if (!results.length) {
      this.hidePanel()
      return
    }

    this.listTarget.innerHTML = results.map((r) => this.resultRowHtml(r)).join("")
    this.panelTarget.classList.remove("hidden")
  }

  resultRowHtml(r) {
    const href = typeof r.url === "string" && r.url.startsWith("/") ? r.url : "#"
    const thumb = this.thumbnailHtml(r.thumbnail_url)
    return `
      <a href="${href}" class="nav-search-result flex items-center gap-3 px-3 py-2 text-sm last:border-0">
        ${thumb}
        <div class="min-w-0 flex-1">
          <span class="font-medium block truncate">${this.escapeHtml(r.title)}</span>
          <span class="text-xs text-[#7c7ca0]">${this.escapeHtml(r.label)}</span>
        </div>
      </a>
    `
  }

  thumbnailHtml(thumbnailUrl) {
    if (typeof thumbnailUrl === "string" && thumbnailUrl.length > 0) {
      const src = this.escapeHtml(thumbnailUrl)
      return `<img src="${src}" alt="" class="w-10 h-10 rounded object-cover flex-shrink-0 bg-[#1a1a2e]" loading="lazy" />`
    }

    return `<div class="w-10 h-10 rounded flex-shrink-0 bg-gradient-to-br from-purple-500 via-indigo-500 to-purple-700" aria-hidden="true"></div>`
  }

  escapeHtml(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }

  setSearching(active) {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.toggle("is-searching", active)
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("hidden", !active)
    }
  }

  activateWrapper() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.add("is-active")
    }
  }

  deactivateWrapper() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.remove("is-active")
      this.wrapperTarget.classList.remove("is-searching")
    }
  }

  hidePanel() {
    this.panelTarget.classList.add("hidden")
    this.listTarget.innerHTML = ""
    this.deactivateWrapper()
  }

  disconnect() {
    clearTimeout(this.timeout)
    clearTimeout(this.hideTimeout)
  }
}
